<template>
  <aside
    v-if="showSideBar"
    class="sidebar-rail"
  >
    <el-menu class="sidebar-menu" :default-active="$route.path" collapse>
      <el-menu-item v-if="map" index="/map" @click="navigateTo('/map')">
        <lucide-icon name="map" />
        <span slot="title">{{ $t('route.map') }}</span>
      </el-menu-item>
      <el-menu-item
        ref="assistantMenuItem"
        :class="{ 'assistant-intro-active': assistantIntroVisible }"
        index="/chat"
        :aria-label="$t('assistant.askAnything')"
        @click="navigateTo('/chat')"
      >
        <lucide-icon name="bot" />
        <span slot="title">{{ $t('assistant.askAnything') }}</span>
      </el-menu-item>
      <el-menu-item v-if="partnerData.legacyMap" index="/legacy" @click="navigateTo('/legacy')">
        <span slot="title">{{ $t('Legacy Map') }}</span>
        <lucide-icon name="map-pinned" />
      </el-menu-item>
      <el-menu-item index="/dashboard" @click="openDashboard">
        <lucide-icon name="dashboard" />
        <span slot="title">{{ $t('route.dashboard') }}</span>
      </el-menu-item>
      <el-menu-item v-if="reports" index="reports-action" @click="openReports">
        <lucide-icon name="file-text" />
        <span slot="title">{{ $t('route.reports') }}</span>
      </el-menu-item>
      <el-menu-item
        v-if="reports && (user.attributes.partnerReports || partnerData.partnerReports)"
        index="partner-reports-action"
        @click="openPartnerReports"
      >
        <lucide-icon name="ticket" />
        <span slot="title">{{ $t('Relatórios personalizados') }}</span>
      </el-menu-item>
      <el-menu-item v-if="manageSchedules" index="/schedules" @click="navigateTo('/schedules')">
        <lucide-icon name="calendar" />
        <span slot="title">{{ $t('route.scheduler') }}</span>
      </el-menu-item>
      <el-menu-item v-if="partnerData.externalReports" index="/external-reports" @click="navigateTo('/external-reports')">
        <i v-if="partnerData.externalReports.icon" :class="partnerData.externalReports.icon"></i>
        <lucide-icon v-else name="files" />
        <span slot="title">{{ partnerData.externalReports.title }}</span>
      </el-menu-item>
      <el-menu-item v-if="settings" index="settings-action" @click="openSettings">
        <lucide-icon name="sliders" />
        <span slot="title">{{ $t('route.settings') }}</span>
      </el-menu-item>
      <el-menu-item v-if="odoo" index="management-action" @click="openOdoo">
        <lucide-icon name="truck" />
        <span slot="title">{{ $t('route.management') }}</span>
      </el-menu-item>
      <el-menu-item v-if="tacho" index="/tacho" @click="navigateTo('/tacho')">
        <span slot="title">{{ $t('Tacógrafo') }}</span>
        <lucide-icon name="gauge" />
      </el-menu-item>
      <el-menu-item index="/routevision" @click="navigateTo('/routevision')">
        <span slot="title">{{ $t('Routes') }}</span>
        <lucide-icon name="route" />
      </el-menu-item>
      <el-menu-item v-if="manual" index="manual-action" @click="openManual">
        <lucide-icon name="book" />
        <span slot="title">{{ $t('route.manual') }}</span>
      </el-menu-item>
      <el-menu-item
        v-for="(mi, index) in extraMenuItems"
        :key="mi.title"
        :index="'extra-' + index"
        @click="open(mi.url)"
      >
        <i :class="mi.class"></i>
        <span slot="title">{{ $t(mi.title) }}</span>
      </el-menu-item>
    </el-menu>
    <span
      v-if="assistantIntroVisible"
      class="assistant-intro"
      :style="assistantIntroStyle"
      aria-hidden="true"
    >
      {{ $t('assistant.askAnything') }}
    </span>
    <div class="sidebar-footer">
      <el-menu class="sidebar-menu sidebar-footer-menu" collapse>
        <el-menu-item index="logout-action" @click="logout">
          <lucide-icon name="logout" />
          <span slot="title">{{ $t('navbar.logout') }}</span>
        </el-menu-item>
      </el-menu>
    </div>
  </aside>
</template>
<script>

import { mapGetters } from 'vuex'
import store from '@/store'
import LucideIcon from '@/components/LucideIcon'
import '../sidebar.scss'
import { getPartnerData } from 'fleetmap-partners'
import {
  dashboardIsActive,
  manualIsActive,
  mapIsActive,
  odooIsActive,
  reportsIsActive,
  settingsIsActive,
  schedulerIsActive
} from '@/utils/modules'
import { Auth } from '@aws-amplify/auth'
import { tachoIsActive } from '@/utils/modules'
import axios from 'axios'

export default {
  components: { LucideIcon },
  data() {
    return {
      assistantIntroStyle: {},
      assistantIntroVisible: false
    }
  },
  computed: {
    ...mapGetters(['user']),
    partnerData() {
      return getPartnerData()
    },
    extraMenuItems() {
      return Object.keys(this.user.attributes).filter(k => k.startsWith('menuItem')).map(i => {
        const values = this.user.attributes[i].split('|')
        return {
          class: values[0],
          title: values[1],
          url: values[2]
        }
      })
    },
    tacho() {
      return tachoIsActive(this.user)
    },
    href() {
      const userUrl = this.user.attributes.partnerReports &&
        this.user.attributes.partnerReports.startsWith &&
        this.user.attributes.partnerReports.startsWith('/')
      if (userUrl) {
        return this.user.attributes.partnerReports
      }
      if (this.user.attributes.partnerReports) {
        return '/reports/partnerReports/'
      }
      return this.partnerData.partnerReports
    },
    manageSchedules() {
      return schedulerIsActive(store.getters.user)
    },
    odoo() {
      return odooIsActive(store.getters.user)
    },
    manual() {
      return manualIsActive()
    },
    dashboard() {
      return dashboardIsActive(store.getters.user)
    },
    reports() {
      return reportsIsActive(store.getters.user)
    },
    settings() {
      return settingsIsActive(store.getters.user)
    },
    map() {
      return mapIsActive(store.getters.user)
    },
    showSideBar() {
      return !this.$route.path.includes('settings')
    }
  },
  mounted() {
    this.showAssistantIntroOnce()
    this.$nextTick(this.positionAssistantIntro)
    window.addEventListener('resize', this.positionAssistantIntro)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.positionAssistantIntro)
  },
  methods: {
    showAssistantIntroOnce() {
      const userId = this.user.id || this.user.email
      if (!userId) return
      const storageKey = `assistant-intro-seen:${userId}`
      try {
        if (window.localStorage.getItem(storageKey)) return
        window.localStorage.setItem(storageKey, '1')
      } catch (e) {
        // Keep the intro usable when browser storage is unavailable.
      }
      this.assistantIntroVisible = true
    },
    positionAssistantIntro() {
      const item = this.$refs.assistantMenuItem && this.$refs.assistantMenuItem.$el
      if (!item) return
      const bounds = item.getBoundingClientRect()
      this.assistantIntroStyle = {
        left: `${bounds.right + 8}px`,
        top: `${bounds.top + bounds.height / 2}px`
      }
    },
    open: (url) => window.open(url),
    navigateTo(path) {
      if (this.$route.path !== path) {
        this.$router.push(path)
      }
    },
    async openDashboard() {
      if (this.partnerData.partnerId === 12) {
        const cookie = await axios.get('/pinmeapi/cookie/get', { withCredentials: true }).then(d => d.data)
        window.open('https://dashboard.afconsultingsystems.com?token=' + cookie.replace('JSESSIONID=', ''))
      } else {
        this.$router.push('/dashboard')
      }
    },
    openReports() {
      window.location.href = '/reports/'
    },
    openPartnerReports() {
      window.open(this.href, '_blank')
    },
    openManual() {
      window.open('/manual')
    },
    openSettings() {
      this.$router.push('/settings/vehicles')
    },
    async openOdoo() {
      const partnerData = getPartnerData(window.location.hostname)
      if (partnerData && partnerData.partnerId === 5) {
        window.open('https://bt.digimherp.ma')
      } else if (this.user.attributes.odoo !== true && this.user.attributes.odoo !== 'true') {
        window.open(this.user.attributes.odoo)
      } else {
        const session = await Auth.currentSession()
        console.log(session)
        window.open(`/odoo/?clientId=${partnerData && partnerData.cognitoClientId}&sub=${session.accessToken.payload.sub}`)
      }
    },
    async logout() {
      this.$store.commit('transient/SET_LOADING', true)
      await this.$store.dispatch('user/logout')
      window.location.href = '/'
    }
  }
}
</script>
